<script setup>
/* global axios */
// Card da Meta Conversions API na página de Integrações — no MESMO padrão dos
// demais (logo + status + descrição + "Configurar"). O formulário abre num
// popup ao clicar em Configurar. Self-contained (axios direto).
import { onMounted, reactive, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import { getCurrentCurrency } from 'dashboard/routes/dashboard/kanban/appointments/constants';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/astrachat/meta_capi_config`;
const inboxBase = `${base}/inbox_configs`;
// default = moeda da conta (config única de moeda), ainda editável aqui
const defaultCurrency = () => getCurrentCurrency() || 'BRL';

const showModal = ref(false);
const saving = ref(false);
const checking = ref(false);
const statusResult = ref(null); // { connected, pixel_name, error }
const tokenSet = ref(false);
const enabled = ref(false);
const form = reactive({
  pixel_id: '',
  access_token: '',
  currency: defaultCurrency(),
  test_event_code: '',
  enabled: false,
  send_organic_events: false,
});

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const inboxIntegrations = ref([]);
const inboxForm = reactive({
  id: null,
  inbox_id: '',
  pixel_id: '',
  access_token: '',
  currency: defaultCurrency(),
  test_event_code: '',
  whatsapp_business_account_id: '',
  page_id: '',
  enabled: false,
  send_organic_events: false,
});
const savingInbox = ref(false);
const editingInboxId = ref(null);

const availableInboxes = computed(() => {
  const used = new Set(inboxIntegrations.value.map(i => i.inbox_id));
  return [{ id: '', name: t('META_CAPI.INBOX_DEFAULT') }].concat(
    inboxes.value.filter(i => !used.has(i.id) || i.id === inboxForm.inbox_id)
  );
});

const load = async () => {
  try {
    const { data } = await axios.get(base);
    const d = data.data || {};
    form.pixel_id = d.pixel_id || '';
    form.currency = d.currency || defaultCurrency();
    form.test_event_code = d.test_event_code || '';
    form.enabled = d.enabled || false;
    form.send_organic_events = d.send_organic_events || false;
    tokenSet.value = d.access_token_set || false;
    enabled.value = d.enabled || false;
  } catch (e) {
    /* silencioso */
  }
};

const loadInboxIntegrations = async () => {
  try {
    const { data } = await axios.get(inboxBase);
    inboxIntegrations.value = data.data || [];
  } catch (e) {
    inboxIntegrations.value = [];
  }
};

const resetInboxForm = () => {
  inboxForm.id = null;
  inboxForm.inbox_id = '';
  inboxForm.pixel_id = '';
  inboxForm.access_token = '';
  inboxForm.currency = defaultCurrency();
  inboxForm.test_event_code = '';
  inboxForm.whatsapp_business_account_id = '';
  inboxForm.page_id = '';
  inboxForm.enabled = false;
  inboxForm.send_organic_events = false;
  editingInboxId.value = null;
};

const openModal = () => {
  form.access_token = '';
  statusResult.value = null;
  resetInboxForm();
  store.dispatch('inboxes/get');
  loadInboxIntegrations();
  showModal.value = true;
};

const checkStatus = async () => {
  checking.value = true;
  statusResult.value = null;
  try {
    const { data } = await axios.get(`${base}/status`);
    statusResult.value = data.data || { connected: false };
  } catch (e) {
    statusResult.value = {
      connected: false,
      error: e.response?.data?.error || t('META_CAPI.SAVE_ERROR'),
    };
  } finally {
    checking.value = false;
  }
};

const save = async () => {
  saving.value = true;
  const payload = {
    pixel_id: form.pixel_id,
    currency: form.currency,
    test_event_code: form.test_event_code,
    enabled: form.enabled,
    send_organic_events: form.send_organic_events,
  };
  if (form.access_token) payload.access_token = form.access_token;
  try {
    const { data } = await axios.put(base, { meta_capi_config: payload });
    const d = data.data || {};
    tokenSet.value = d.access_token_set || false;
    enabled.value = d.enabled || false;
    form.access_token = '';
    statusResult.value = null;
    showModal.value = false;
    useAlert(t('META_CAPI.SAVED'));
  } catch (e) {
    useAlert(e.response?.data?.error || t('META_CAPI.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const editInboxIntegration = integration => {
  Object.assign(inboxForm, {
    id: integration.id,
    inbox_id: integration.inbox_id || '',
    pixel_id: integration.pixel_id || '',
    access_token: '',
    currency: integration.currency || defaultCurrency(),
    test_event_code: integration.test_event_code || '',
    whatsapp_business_account_id:
      integration.whatsapp_business_account_id || '',
    page_id: integration.page_id || '',
    enabled: integration.enabled || false,
    send_organic_events: integration.send_organic_events || false,
  });
  editingInboxId.value = integration.id;
};

const saveInboxIntegration = async () => {
  savingInbox.value = true;
  const payload = {
    inbox_id: inboxForm.inbox_id || null,
    pixel_id: inboxForm.pixel_id,
    access_token: inboxForm.access_token,
    currency: inboxForm.currency,
    test_event_code: inboxForm.test_event_code,
    whatsapp_business_account_id: inboxForm.whatsapp_business_account_id,
    page_id: inboxForm.page_id,
    enabled: inboxForm.enabled,
    send_organic_events: inboxForm.send_organic_events,
  };
  try {
    if (editingInboxId.value) {
      await axios.patch(`${inboxBase}/${editingInboxId.value}`, {
        meta_capi_inbox_config: payload,
      });
    } else {
      await axios.post(inboxBase, { meta_capi_inbox_config: payload });
    }
    resetInboxForm();
    await loadInboxIntegrations();
    useAlert(t('META_CAPI.SAVED'));
  } catch (e) {
    useAlert(e.response?.data?.error || t('META_CAPI.SAVE_ERROR'));
  } finally {
    savingInbox.value = false;
  }
};

const deleteInboxIntegration = async id => {
  if (!window.confirm(t('META_CAPI.INBOX_DELETE_CONFIRM'))) return;
  try {
    await axios.delete(`${inboxBase}/${id}`);
    await loadInboxIntegrations();
    if (editingInboxId.value === id) resetInboxForm();
    useAlert(t('META_CAPI.INBOX_DELETED'));
  } catch (e) {
    useAlert(e.response?.data?.error || t('META_CAPI.SAVE_ERROR'));
  }
};

onMounted(() => {
  load();
  store.dispatch('inboxes/get');
});
</script>

<template>
  <div
    class="flex flex-col flex-1 p-4 m-px outline outline-n-container outline-1 bg-n-card rounded-xl"
  >
    <div class="flex items-start justify-between">
      <div
        class="flex items-center justify-center w-12 h-12 mb-2 border rounded-md shadow-sm border-n-weak bg-n-alpha-3"
      >
        <span class="i-lucide-target size-6 text-n-brand" />
      </div>
      <Label
        :label="
          enabled
            ? t('META_CAPI.STATUS.CONFIGURED')
            : t('META_CAPI.STATUS.PENDING')
        "
        :color="enabled ? 'teal' : 'slate'"
        compact
      />
    </div>
    <div class="flex flex-col flex-1 m-0">
      <div
        class="flex items-center justify-between mb-2 font-medium text-n-slate-12"
      >
        <span class="text-heading-3 text-n-slate-12">
          {{ t('META_CAPI.CARD_NAME') }}
        </span>
        <NextButton
          :label="t('META_CAPI.CONFIGURE')"
          icon="i-woot-settings"
          link
          xs
          @click="openModal"
        />
      </div>
      <p class="text-n-slate-11 text-body-main">
        {{ t('META_CAPI.DESCRIPTION') }}
      </p>
    </div>

    <woot-modal v-model:show="showModal" :on-close="() => (showModal = false)">
      <div class="flex flex-col h-auto overflow-auto w-[40rem] max-w-full">
        <woot-modal-header
          :header-title="t('META_CAPI.CARD_NAME')"
          :header-content="t('META_CAPI.DESCRIPTION')"
        />
        <div class="flex flex-col gap-4 px-8 py-4">
          <!-- Configuração global / fallback -->
          <form class="flex flex-col gap-2" @submit.prevent="save">
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('META_CAPI.GLOBAL_CONFIG') }}
            </p>
            <woot-input
              v-model="form.pixel_id"
              class="w-full"
              autocomplete="off"
              :label="t('META_CAPI.FORM.PIXEL_ID.LABEL')"
              :placeholder="t('META_CAPI.FORM.PIXEL_ID.PLACEHOLDER')"
            />
            <woot-input
              v-model="form.access_token"
              type="password"
              class="w-full"
              autocomplete="new-password"
              :label="t('META_CAPI.FORM.TOKEN.LABEL')"
              :placeholder="
                tokenSet
                  ? t('META_CAPI.FORM.TOKEN.SET')
                  : t('META_CAPI.FORM.TOKEN.PLACEHOLDER')
              "
              :help-text="tokenSet ? t('META_CAPI.FORM.TOKEN.KEEP') : ''"
            />
            <div class="grid grid-cols-2 gap-3">
              <woot-input
                v-model="form.currency"
                class="w-full"
                autocomplete="off"
                :label="t('META_CAPI.FORM.CURRENCY.LABEL')"
                :placeholder="defaultCurrency()"
              />
              <woot-input
                v-model="form.test_event_code"
                class="w-full"
                autocomplete="off"
                :label="t('META_CAPI.FORM.TEST_CODE.LABEL')"
                :placeholder="t('META_CAPI.FORM.TEST_CODE.PLACEHOLDER')"
                :help-text="t('META_CAPI.FORM.TEST_CODE.HELP')"
              />
            </div>
            <label class="flex items-center gap-2 py-2 text-sm text-n-slate-12">
              <input
                v-model="form.enabled"
                type="checkbox"
                class="accent-n-brand"
              />
              {{ t('META_CAPI.FORM.ENABLED.LABEL') }}
            </label>
            <label class="flex items-center gap-2 py-2 text-sm text-n-slate-12">
              <input
                v-model="form.send_organic_events"
                type="checkbox"
                class="accent-n-brand"
              />
              {{ t('META_CAPI.FORM.SEND_ORGANIC_EVENTS.LABEL') }}
            </label>
            <!-- resultado do "Verificar status" -->
            <div
              v-if="statusResult"
              class="flex items-start gap-2 p-2 mt-1 text-xs rounded-lg"
              :class="
                statusResult.connected
                  ? 'bg-n-teal-3 text-n-teal-11'
                  : 'bg-n-ruby-2 text-n-ruby-11'
              "
            >
              <span
                class="mt-0.5 size-3.5 shrink-0"
                :class="
                  statusResult.connected
                    ? 'i-lucide-badge-check'
                    : 'i-lucide-badge-alert'
                "
              />
              <span v-if="statusResult.connected">
                <template v-if="statusResult.pixel_name">
                  {{
                    t('META_CAPI.CHECK.CONNECTED_WITH_NAME', {
                      name: statusResult.pixel_name,
                    })
                  }}
                </template>
                <template v-else>
                  {{ t('META_CAPI.CHECK.CONNECTED') }}
                </template>
              </span>
              <span v-else>
                {{
                  t('META_CAPI.CHECK.DISCONNECTED_WITH_ERROR', {
                    error: statusResult.error,
                  })
                }}
              </span>
            </div>

            <div class="flex items-center justify-between w-full gap-2 py-2">
              <NextButton
                faded
                slate
                icon="i-lucide-plug-zap"
                :label="t('META_CAPI.CHECK.BUTTON')"
                :is-loading="checking"
                @click.prevent="checkStatus"
              />
              <div class="flex items-center gap-2">
                <NextButton
                  faded
                  slate
                  type="reset"
                  :label="t('META_CAPI.FORM.CANCEL')"
                  @click.prevent="showModal = false"
                />
                <NextButton
                  type="submit"
                  :label="t('META_CAPI.FORM.SAVE')"
                  :is-loading="saving"
                />
              </div>
            </div>
          </form>

          <div class="border-t border-n-weak" />

          <!-- Configurações por inbox -->
          <div class="flex flex-col gap-3">
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('META_CAPI.INBOX_CONFIGS_TITLE') }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ t('META_CAPI.INBOX_CONFIGS_HINT') }}
            </p>

            <div
              v-if="inboxIntegrations.length > 0"
              class="border rounded-lg border-n-weak overflow-hidden"
            >
              <table class="w-full text-sm">
                <thead class="bg-n-solid-2">
                  <tr>
                    <th
                      class="px-3 py-2 text-left text-xs font-medium text-n-slate-11"
                    >
                      {{ t('META_CAPI.INBOX_TABLE.INBOX') }}
                    </th>
                    <th
                      class="px-3 py-2 text-left text-xs font-medium text-n-slate-11"
                    >
                      {{ t('META_CAPI.INBOX_TABLE.PIXEL') }}
                    </th>
                    <th
                      class="px-3 py-2 text-left text-xs font-medium text-n-slate-11"
                    >
                      {{ t('META_CAPI.INBOX_TABLE.STATUS') }}
                    </th>
                    <th
                      class="px-3 py-2 text-right text-xs font-medium text-n-slate-11"
                    >
                      {{ t('META_CAPI.INBOX_TABLE.ACTIONS') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in inboxIntegrations"
                    :key="item.id"
                    class="border-t border-n-weak"
                  >
                    <td class="px-3 py-2 text-n-slate-12">
                      {{ item.inbox_name }}
                    </td>
                    <td class="px-3 py-2 font-mono text-n-slate-11">
                      {{ item.pixel_id }}
                    </td>
                    <td class="px-3 py-2">
                      <Label
                        :label="
                          item.enabled
                            ? t('META_CAPI.STATUS.CONFIGURED')
                            : t('META_CAPI.STATUS.PENDING')
                        "
                        :color="item.enabled ? 'teal' : 'slate'"
                        compact
                      />
                    </td>
                    <td class="px-3 py-2 text-right">
                      <NextButton
                        icon="i-lucide-pencil"
                        link
                        xs
                        @click="editInboxIntegration(item)"
                      />
                      <NextButton
                        icon="i-lucide-trash-2"
                        link
                        xs
                        class="text-n-ruby-11"
                        @click="deleteInboxIntegration(item.id)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <form
              class="flex flex-col gap-2 p-3 rounded-lg bg-n-solid-2"
              @submit.prevent="saveInboxIntegration"
            >
              <p class="text-xs font-medium text-n-slate-11">
                {{
                  editingInboxId
                    ? t('META_CAPI.EDIT_INBOX_INTEGRATION')
                    : t('META_CAPI.ADD_INBOX_INTEGRATION')
                }}
              </p>
              <select
                v-model="inboxForm.inbox_id"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              >
                <option
                  v-for="inbox in availableInboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
              <woot-input
                v-model="inboxForm.pixel_id"
                class="w-full"
                autocomplete="off"
                :label="t('META_CAPI.FORM.PIXEL_ID.LABEL')"
                :placeholder="t('META_CAPI.FORM.PIXEL_ID.PLACEHOLDER')"
              />
              <woot-input
                v-model="inboxForm.access_token"
                type="password"
                class="w-full"
                autocomplete="new-password"
                :label="t('META_CAPI.FORM.TOKEN.LABEL')"
                :placeholder="t('META_CAPI.FORM.TOKEN.PLACEHOLDER')"
              />
              <div class="grid grid-cols-2 gap-3">
                <woot-input
                  v-model="inboxForm.currency"
                  class="w-full"
                  autocomplete="off"
                  :label="t('META_CAPI.FORM.CURRENCY.LABEL')"
                  :placeholder="defaultCurrency()"
                />
                <woot-input
                  v-model="inboxForm.test_event_code"
                  class="w-full"
                  autocomplete="off"
                  :label="t('META_CAPI.FORM.TEST_CODE.LABEL')"
                  :placeholder="t('META_CAPI.FORM.TEST_CODE.PLACEHOLDER')"
                />
              </div>
              <woot-input
                v-model="inboxForm.whatsapp_business_account_id"
                class="w-full"
                autocomplete="off"
                :label="t('META_CAPI.FORM.WABA_ID.LABEL')"
                :placeholder="t('META_CAPI.FORM.WABA_ID.PLACEHOLDER')"
                :help-text="t('META_CAPI.FORM.WABA_ID.HELP')"
              />
              <woot-input
                v-model="inboxForm.page_id"
                class="w-full"
                autocomplete="off"
                :label="t('META_CAPI.FORM.PAGE_ID.LABEL')"
                :placeholder="t('META_CAPI.FORM.PAGE_ID.PLACEHOLDER')"
                :help-text="t('META_CAPI.FORM.PAGE_ID.HELP')"
              />
              <label
                class="flex items-center gap-2 py-1 text-sm text-n-slate-12"
              >
                <input
                  v-model="inboxForm.enabled"
                  type="checkbox"
                  class="accent-n-brand"
                />
                {{ t('META_CAPI.FORM.ENABLED.LABEL') }}
              </label>
              <label
                class="flex items-center gap-2 py-1 text-sm text-n-slate-12"
              >
                <input
                  v-model="inboxForm.send_organic_events"
                  type="checkbox"
                  class="accent-n-brand"
                />
                {{ t('META_CAPI.FORM.SEND_ORGANIC_EVENTS.LABEL') }}
              </label>
              <div class="flex items-center justify-end gap-2 pt-1">
                <NextButton
                  v-if="editingInboxId"
                  faded
                  slate
                  type="button"
                  :label="t('META_CAPI.FORM.CANCEL')"
                  @click.prevent="resetInboxForm"
                />
                <NextButton
                  type="submit"
                  :label="
                    editingInboxId
                      ? t('META_CAPI.FORM.SAVE')
                      : t('META_CAPI.ADD_INBOX_INTEGRATION')
                  "
                  :is-loading="savingInbox"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </woot-modal>
  </div>
</template>
